import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, MaskReveal } from "./Reveal";
import { IMAGES, PHONE_TEL, PHONE_DISPLAY } from "../../lib/site";

const CHAPTERS = [
  { n: "01", t: "A neighbor, not an app", d: "KAT Transportation is run by Rui Godoy — a familiar face who has spent years driving the people of Haymarket where they need to go." },
  { n: "02", t: "Built on trust", d: "This is a family-operated business. We measure success by the long-term relationships we build with every client we serve." },
  { n: "03", t: "Personal by design", d: "No surge pricing, no strangers, no guesswork. Just dependable, courteous rides from someone who genuinely cares." },
];

export const About = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="about" data-testid="about-section" className="relative py-24 md:py-32 bg-[#0a0a0a] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
          {/* Image column */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <Reveal className="relative">
              <div className="relative overflow-hidden rounded-2xl border border-white/10 aspect-[4/5]">
                <motion.img
                  style={{ y }}
                  src={IMAGES.driver}
                  alt="Rui Godoy, professional chauffeur and owner of KAT Transportation"
                  className="w-full h-[112%] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              </div>
              <div className="absolute -bottom-6 -right-4 md:right-6 bg-[#D4AF37] text-black rounded-xl px-6 py-4 shadow-xl">
                <div className="font-serif-display text-3xl leading-none">Rui Godoy</div>
                <div className="text-xs tracking-[0.15em] uppercase mt-1 font-semibold">Owner &amp; Operator</div>
              </div>
            </Reveal>
          </div>

          {/* Text column */}
          <div className="lg:col-span-7">
            <span className="flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] font-semibold mb-5">
              <span className="h-px w-10 bg-[#D4AF37]" /> Our Story
            </span>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.03]">
              <MaskReveal lines={["The family behind", "every mile."]} />
            </h2>

            <div className="mt-12 space-y-10">
              {CHAPTERS.map((c, i) => (
                <Reveal key={c.n} delay={i * 0.08} className="flex gap-6 border-l border-white/10 pl-6">
                  <span className="font-serif-display text-[#D4AF37]/70 text-xl leading-none pt-1">{c.n}</span>
                  <div>
                    <h3 className="font-serif-display text-2xl text-white">{c.t}</h3>
                    <p className="text-neutral-400 mt-2 leading-relaxed max-w-xl">{c.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.1} className="mt-12">
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="about-call-button"
                className="inline-flex items-center gap-3 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] px-7 py-3.5 font-medium hover:bg-[#D4AF37] hover:text-black transition-colors duration-300"
              >
                Ride with us — {PHONE_DISPLAY}
              </a>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
